import { useMemo, useState } from 'react';

import { Close, CopyAll, OpenInNew } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  MaterialReactTable,
  type MRT_Localization,
  type MRT_PaginationState,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { If } from 'src/components/If';

import { TotalizationCards } from './TotalizationCards';
import { exportTableDataAsText, type MRTTableConfig } from './utils';

const URL_COLUMN_KEYS = ['url'];

function isUrlColumn(accessorKey: string, header: string): boolean {
  const key = (accessorKey || header || '').trim().toLowerCase();
  return URL_COLUMN_KEYS.includes(key);
}

type TotalizationsProps = {
  title?: string;
  open: boolean;
  onClose: () => void;
  isLoading: boolean;
  processed?: number;
  found?: number;
  notFounds?: number;
  /** Configuração vinda de setColumnsByData(parseDataBySeparatorType(...)); quando não informada, a tabela fica vazia. */
  tableConfig?: MRTTableConfig | null;
};

export function Totalizations({
  title,
  open,
  onClose,
  isLoading,
  processed,
  found,
  notFounds,
  tableConfig = null,
}: TotalizationsProps) {
  const history = useHistory();
  const baseColumns = tableConfig?.columns ?? [];
  const data = tableConfig?.data ?? [];

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const columns = useMemo(
    () =>
      baseColumns.map((col) => {
        if (!isUrlColumn(col.accessorKey ?? '', col.header ?? '')) return col;
        return {
          ...col,
          Cell: ({ cell }: { cell: { getValue: () => unknown } }) => {
            const value = String(cell.getValue() ?? '').trim();
            if (value === '' || value === 'Não encontrado') {
              return value || '—';
            }
            return (
              <Box sx={{ display: 'flex', gap: 1, flexDirection: 'row' }}>
                <Tooltip title="Ver operação">
                  <IconButton
                    size="small"
                    aria-label="Ver operação"
                    color="primary"
                    onClick={() => {
                      window.open(value, '_blank');
                    }}>
                    <OpenInNew fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Copiar URL">
                  <IconButton
                    size="small"
                    aria-label="Copiar URL"
                    color="info"
                    onClick={() => {
                      void navigator.clipboard.writeText(value);
                    }}>
                    <CopyAll fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          },
        };
      }),
    [baseColumns, history],
  );

  const table = useMaterialReactTable({
    columns,
    data,
    localization: {
      ...MRT_Localization_PT_BR,
      exportDataAsText: 'Exportar como texto',
    } as MRT_Localization,
    enableTopToolbar: false,
    enableColumnActions: false,
    enableFullScreenToggle: false,
    enableDensityToggle: false,
    enableHiding: false,
    enableSorting: false,
    enablePagination: true,
    state: { pagination },
    onPaginationChange: setPagination,
    muiTableContainerProps: { sx: { maxHeight: 400 } },
  });

  const tableExportedText = useMemo(
    () =>
      exportTableDataAsText({
        columns: baseColumns,
        data,
      }),
    [baseColumns, data],
  );

  const handleCopyAndPasteData = () => {
    void navigator.clipboard.writeText(tableExportedText);

    toast.success('Copiado com sucesso!');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', fontSize: '24px' }}>
        {title ?? 'Totalizadores'}
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              pt: 1,
            }}>
            <If condition={!!processed && !!found && !!notFounds}>
              <TotalizationCards
                processed={processed ?? 0}
                found={found ?? 0}
                notFounds={notFounds ?? 0}
              />
            </If>
            <MaterialReactTable table={table} />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          startIcon={<Close />}
          variant="text"
          color="primary"
          onClick={onClose}
          disabled={isLoading}>
          Fechar
        </Button>
        <Button
          startIcon={<CopyAll />}
          variant="contained"
          color="primary"
          onClick={handleCopyAndPasteData}
          disabled={isLoading}>
          Copiar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
